/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import * as UserActionCreators from '@app/actions/UserActionCreators';
import {Nagbar} from '@app/components/layout/Nagbar';
import {NagbarButton} from '@app/components/layout/NagbarButton';
import {NagbarContent} from '@app/components/layout/NagbarContent';
import {UserSettingsModal} from '@app/components/modals/UserSettingsModal';
import UserStore from '@app/stores/UserStore';
import {getFormattedFullDate} from '@app/utils/DateUtils';
import {UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

function useOnboardingMessage(): React.ReactNode {
	const user = UserStore.currentUser;

	return useMemo(() => {
		if (!user) {
			return <Trans>Welcome to Fluxer Plutonium! Explore your premium features and manage your subscription.</Trans>;
		}

		const isTrialUser = user.isOutOfBandTrialActive();
		const isVisionary = user.premiumType === UserPremiumTypes.LIFETIME;
		const billingCycle = user.premiumBillingCycle;
		const premiumUntil = user.premiumUntil;
		const willCancel = user.premiumWillCancel;

		if (isTrialUser) {
			const trialEndsAt = user.premiumOutOfBandTrialEndsAt;
			if (trialEndsAt) {
				const formattedDate = getFormattedFullDate(trialEndsAt);
				return (
					<Trans>
						Welcome to your Plutonium trial! Your trial runs until <strong>{formattedDate}</strong>. Explore your
						premium features and upgrade anytime to keep them.
					</Trans>
				);
			}
			return (
				<Trans>Welcome to your Plutonium trial! Explore your premium features and upgrade anytime to keep them.</Trans>
			);
		}

		if (isVisionary) {
			return (
				<Trans>
					Welcome to Fluxer Plutonium! You have lifetime access as a Visionary. Explore your premium features.
				</Trans>
			);
		}

		if (willCancel && premiumUntil) {
			const formattedDate = getFormattedFullDate(premiumUntil);
			return (
				<Trans>
					Welcome to Fluxer Plutonium! Your subscription is active until <strong>{formattedDate}</strong>. Explore your
					premium features and manage your subscription.
				</Trans>
			);
		}

		if (billingCycle && premiumUntil) {
			const formattedDate = getFormattedFullDate(premiumUntil);
			if (billingCycle === 'monthly') {
				return (
					<Trans>
						Welcome to Fluxer Plutonium! Your monthly subscription renews on <strong>{formattedDate}</strong>. Explore
						your premium features and manage your subscription.
					</Trans>
				);
			}
			return (
				<Trans>
					Welcome to Fluxer Plutonium! Your yearly subscription renews on <strong>{formattedDate}</strong>. Explore your
					premium features and manage your subscription.
				</Trans>
			);
		}

		if (premiumUntil) {
			const formattedDate = getFormattedFullDate(premiumUntil);
			return (
				<Trans>
					Welcome to Fluxer Plutonium! Your gift subscription runs until <strong>{formattedDate}</strong>. Explore your
					premium features.
				</Trans>
			);
		}

		return <Trans>Welcome to Fluxer Plutonium! Explore your premium features and manage your subscription.</Trans>;
	}, [user]);
}

export const PremiumOnboardingNagbar = observer(function PremiumOnboardingNagbar({isMobile}: {isMobile: boolean}) {
	const message = useOnboardingMessage();

	const handleOpenPremiumSettings = useCallback(() => {
		void UserActionCreators.update({has_dismissed_premium_onboarding: true});
		ModalActionCreators.push(modal(() => <UserSettingsModal initialTab="plutonium" />));
	}, []);

	const handleDismiss = useCallback(() => {
		void UserActionCreators.update({has_dismissed_premium_onboarding: true});
	}, []);

	return (
		<Nagbar
			isMobile={isMobile}
			backgroundColor="var(--brand-primary)"
			textColor="var(--text-on-brand-primary)"
			dismissible
			onDismiss={handleDismiss}
		>
			<NagbarContent
				isMobile={isMobile}
				onDismiss={handleDismiss}
				message={message}
				actions={
					<NagbarButton isMobile={isMobile} onClick={handleOpenPremiumSettings}>
						<Trans>View Premium Features</Trans>
					</NagbarButton>
				}
			/>
		</Nagbar>
	);
});
